import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { brand as defaultBrand, type BrandConfig } from "./brand.config";
import { deriveAccentTokens } from "./colors";

interface BrandContextValue {
  brand: BrandConfig;
  /** Update the live brand (re-skins the whole app; persisted locally). */
  setBrand: (patch: Partial<BrandConfig>) => void;
}

export const BrandContext = createContext<BrandContextValue>({
  brand: defaultBrand,
  setBrand: () => {},
});

const STORAGE_KEY = "ledgerline-brand";

/** RTL locales get dir="rtl" on the document (ARCHITECTURE §6). */
function isRtl(locale: string): boolean {
  return /^(ar|he|fa|ur)(-|$)/i.test(locale);
}

function loadPersisted(): BrandConfig {
  if (typeof localStorage === "undefined") return defaultBrand;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultBrand;
    const parsed = JSON.parse(raw) as Partial<BrandConfig>;
    return {
      ...defaultBrand,
      ...parsed,
      colors: { ...defaultBrand.colors, ...(parsed.colors ?? {}) },
    };
  } catch {
    return defaultBrand;
  }
}

function prefersDark(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/**
 * Applies the active brand to the running app (ARCHITECTURE §5):
 *  - sets document.title from brand.name
 *  - sets <html> lang + dir (RTL for ar-AE)
 *  - derives `--accent`, `--accent-strong`, `--on-accent`, `--accent-weak` from
 *    the accent's luminance so primary buttons always clear AA contrast
 *  - exposes brand + a live setter via context (see useBrand / useSetBrand)
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrandState] = useState<BrandConfig>(loadPersisted);

  const applyTheme = useCallback((b: BrandConfig) => {
    const root = document.documentElement;
    const tokens = deriveAccentTokens(b.colors.accent, prefersDark());
    root.style.setProperty("--accent", tokens.accent);
    root.style.setProperty("--accent-strong", tokens.accentStrong);
    root.style.setProperty("--on-accent", tokens.onAccent);
    root.style.setProperty("--accent-weak", tokens.accentWeak);
    // Legacy --brand-* contract (kept for any consumer of the raw config).
    for (const [key, value] of Object.entries(b.colors)) {
      root.style.setProperty(`--brand-${key}`, value);
    }
    document.title = b.name;
    root.lang = b.locale;
    root.dir = isRtl(b.locale) ? "rtl" : "ltr";
  }, []);

  useEffect(() => {
    applyTheme(brand);
  }, [brand, applyTheme]);

  // Recompute accent tokens when the OS color scheme flips.
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(brand);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [brand, applyTheme]);

  const setBrand = useCallback((patch: Partial<BrandConfig>) => {
    setBrandState((prev) => {
      const next: BrandConfig = {
        ...prev,
        ...patch,
        colors: { ...prev.colors, ...(patch.colors ?? {}) },
      };
      try {
        localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — live edit still applies for the session */
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ brand, setBrand }), [brand, setBrand]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
